import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { getMelById } from '../../../services/api';
import { changeModalContent, changeModalVisibility } from '../../../models/drawer';
import TFLoading from '../../TFLoading';

const Label = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
`;

const Value = styled.div`
  margin-bottom: 16px;
`;

const LinkButton = styled.button`
  color: ${({ theme }) => theme.colours.blue};
  cursor: pointer;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  font: inherit;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60px;
`;

interface DeferralReferencePopupProps {
  melId: string;
}

const DeferralReferencePopup: React.FC<DeferralReferencePopupProps> = ({ melId }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Prefetch data when component mounts
  useEffect(() => {
    queryClient.prefetchQuery(['mel', melId], () => getMelById(melId));
  }, [melId, queryClient]);

  const {
    data: melData,
    isLoading,
    isFetching,
  } = useQuery(['mel', melId], () => getMelById(melId), {
    enabled: !!melId,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const isLoadingData = isLoading || isFetching;

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(changeModalVisibility({ payload: true }));

    dispatch(
      changeModalContent({
        payload: {
          title: formatMessage({ id: 'text.deferralReferenceDocument' }),
          text: (
            <>
              <Label>{formatMessage({ id: 'text.revisionName' })}</Label>
              {isLoadingData ? (
                <LoadingWrapper data-testid="LoadingWrapper">
                  <TFLoading loading height={40} width={40} />
                </LoadingWrapper>
              ) : (
                <Value>{melData?.revision_code || '-'}</Value>
              )}
            </>
          ),
          hideCancel: false,
          saveButtonText: formatMessage({ id: 'text.close' }),
          saveAction: () => {
            dispatch(changeModalVisibility({ payload: false }));
          },
        },
      }),
    );
  };

  return (
    <TFLoading loading={isLoadingData} contain width={20} height={20}>
      <LinkButton onClick={handleOpen} disabled={isLoadingData}>
        {formatMessage({ id: 'text.deferralReferenceDocument' })}
      </LinkButton>
    </TFLoading>
  );
};

export default DeferralReferencePopup;
